import React from 'react';
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { UserIcon, Sparkles, GraduationCap } from "lucide-react";
import { motion } from "framer-motion";
import { Owl } from '../visual/Owl';
import { useTranslations } from '../../i18n/hooks';

export const SleepConsultantSection: React.FC = () => {
  const t = useTranslations('index');

  return (
    <motion.div 
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="relative overflow-hidden p-6 flex items-center justify-center role-card"
    >
      {/* Dark mode animated background with image */}
      <div 
        className="absolute inset-0 transition-all duration-1000 dark:block light:hidden"
        style={{
          backgroundImage: `
            radial-gradient(circle at 50% 50%, 
              rgba(49, 46, 129, 0.3) 0%,
              rgba(10, 15, 30, 0.9) 100%
            ),
            url('/NicoleSleep.png')
          `,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          filter: 'brightness(0.9) contrast(1.1)',
        }}
      />

      {/* Light mode background - new gradient background */}
      <div 
        className="absolute inset-0 transition-all duration-1000 light:block dark:hidden"
        style={{
          backgroundImage: `
            linear-gradient(135deg, #f9f4ff 0%, #f0e5ff 100%)
          `,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }}
      />

      {/* Content */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.8 }}
        className="relative max-w-md mx-auto space-y-8 text-center z-10"
      >
        <div className="relative backdrop-blur-xl dark:bg-slate-900/70 light:bg-white/70 p-8 rounded-3xl 
                      dark:border dark:border-indigo-500/50 light:border light:border-indigo-200 shadow-2xl
                      dark:hover:border-indigo-400/60 light:hover:border-indigo-300 transition-all duration-300
                      dark:shadow-[0_0_25px_rgba(99,102,241,0.2)] light:shadow-[0_10px_30px_rgba(138,67,179,0.1)]">
          <div className="transform transition-transform group-hover:scale-105 mb-6 role-image-container">
            <Owl className="w-24 h-24 mx-auto dark:text-indigo-300 light:text-indigo-600" />
          </div>
          <div className="space-y-5 mb-8">
            <motion.h2 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-3xl font-bold dark:text-white light:text-slate-800 drop-shadow-sm role-card-title"
            >
              {t('sleepCoach.title')}
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="dark:text-gray-100 light:text-slate-600 leading-relaxed drop-shadow-sm role-card-description"
            >
              {t('sleepCoach.description')}
            </motion.p>
          </div>

          <div className="space-y-4">
            <Button
              asChild
              variant="high-contrast"
              className="w-full dark:bg-gradient-to-r dark:from-indigo-600 dark:to-purple-600 dark:text-white light:bg-purple-600 light:text-white font-medium text-lg py-6
                       dark:hover:from-indigo-700 dark:hover:to-purple-700 light:hover:bg-purple-700
                       transition-all duration-300 shadow-lg hover:shadow-indigo-500/25 hover:scale-[1.02]
                       active:scale-[0.98] group relative overflow-hidden coach-sign-in"
            >
              <Link to="/coach-signin" className="inline-flex items-center justify-center gap-2">
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                <UserIcon className="w-5 h-5 relative z-10" />
                <span className="relative z-10 drop-shadow-sm">{t('sleepCoach.signIn')}</span>
              </Link>
            </Button>
            <Button
              asChild
              variant="high-contrast-outline"
              className="w-full backdrop-blur-sm border-2 border-white text-white font-medium text-lg py-6
                       hover:bg-white/10 transition-all duration-300
                       shadow-lg hover:shadow-indigo-500/25 hover:scale-[1.02] active:scale-[0.98]
                       relative overflow-hidden group"
            >
              <Link to="/specialist-registration" className="inline-flex items-center justify-center gap-2">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                <GraduationCap className="w-5 h-5 relative z-10" />
                <span className="relative z-10 drop-shadow-sm">{t('sleepCoach.register')}</span>
              </Link>
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
